import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import { formatDate, formatRelativeTime } from '../utils/dateFormat';
import { formatRupiah, formatRupiahCompact } from '../utils/currency';
import { Avatar } from '../utils/avatar';

/* ------------------------------------------------------------------ helpers */

type Tone = { light: string; dark: string; dot: string };

const STATUS_TONES: Record<string, Tone> = {
  NEW: { light: 'bg-blue-50 text-blue-700 border-blue-200', dark: 'bg-blue-900/30 text-blue-300 border-blue-800', dot: 'bg-blue-500' },
  DRAFTING: { light: 'bg-amber-50 text-amber-700 border-amber-200', dark: 'bg-amber-900/30 text-amber-300 border-amber-800', dot: 'bg-amber-500' },
  REVIEW: { light: 'bg-orange-50 text-orange-700 border-orange-200', dark: 'bg-orange-900/30 text-orange-300 border-orange-800', dot: 'bg-orange-500' },
  APPROVED: { light: 'bg-emerald-50 text-emerald-700 border-emerald-200', dark: 'bg-emerald-900/30 text-emerald-300 border-emerald-800', dot: 'bg-emerald-500' },
  RELEASED: { light: 'bg-violet-50 text-violet-700 border-violet-200', dark: 'bg-violet-900/30 text-violet-300 border-violet-800', dot: 'bg-violet-500' },
  CANCELLED: { light: 'bg-rose-50 text-rose-700 border-rose-200', dark: 'bg-rose-900/30 text-rose-300 border-rose-800', dot: 'bg-rose-500' },
};
const DEFAULT_TONE: Tone = { light: 'bg-gray-50 text-gray-700 border-gray-200', dark: 'bg-gray-800 text-gray-300 border-gray-700', dot: 'bg-gray-400' };
const toneFor = (name?: string) => STATUS_TONES[(name || '').toUpperCase()] || DEFAULT_TONE;

const STAGE_ICONS: Record<string, string> = {
  NEW: 'fa-inbox', DRAFTING: 'fa-pen-nib', REVIEW: 'fa-magnifying-glass', APPROVED: 'fa-circle-check', RELEASED: 'fa-paper-plane', CANCELLED: 'fa-ban',
};

/** seconds -> "2h 10j" (hari/jam) or "3j 20m" (jam/menit) */
const formatDuration = (totalSeconds: number): string => {
  const s = Math.abs(totalSeconds);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d > 0) return h > 0 ? `${d}h ${h}j` : `${d}h`;
  if (h > 0) return m > 0 ? `${h}j ${m}m` : `${h}j`;
  return `${Math.max(m, 1)}m`;
};

const SlaText: React.FC<{ sla: any; darkMode: boolean; strong?: boolean }> = ({ sla, darkMode, strong }) => {
  if (!sla) return <span className={darkMode ? 'text-gray-600' : 'text-gray-300'}>–</span>;
  if (sla.remaining_seconds === null || sla.remaining_seconds === undefined) {
    return (
      <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} title="Status ini belum punya SLA">
        {sla.elapsed_seconds != null ? `${formatDuration(sla.elapsed_seconds)} di tahap ini` : '–'}
      </span>
    );
  }
  if (sla.overdue) {
    return (
      <span className={`inline-flex items-center gap-1 ${strong ? 'font-bold' : 'font-semibold'} text-rose-600 ${darkMode ? 'text-rose-400' : ''}`}>
        <i className="fa-solid fa-triangle-exclamation text-[11px]"></i>
        Lewat {formatDuration(sla.remaining_seconds)}
      </span>
    );
  }
  const warn = sla.sla_days && sla.remaining_seconds < sla.sla_days * 86400 * 0.25;
  return (
    <span className={`inline-flex items-center gap-1 tabular-nums ${warn ? (darkMode ? 'text-amber-300 font-semibold' : 'text-amber-600 font-semibold') : (darkMode ? 'text-gray-300' : 'text-gray-700')}`}>
      <i className="fa-regular fa-clock text-[11px] opacity-70"></i>
      {formatDuration(sla.remaining_seconds)}
    </span>
  );
};

const StatusBadge: React.FC<{ name?: string; darkMode: boolean }> = ({ name, darkMode }) => {
  const tone = toneFor(name);
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-md border ${darkMode ? tone.dark : tone.light}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${tone.dot}`}></span>
      {name || 'N/A'}
    </span>
  );
};

/* ------------------------------------------------------------------ page */

const Summary: React.FC = () => {
  const { darkMode } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stageId, setStageId] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [detail, setDetail] = useState<any>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [needsActionOnly, setNeedsActionOnly] = useState(false);
  const [overdueOnly, setOverdueOnly] = useState(false);
  const detailRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const loadSummary = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await apiService.getSummary({ status_id: stageId, search: debouncedSearch });
      setData(res);
      setError(null);
    } catch (err: any) {
      console.error('Error loading summary:', err);
      setError(err.message || 'Gagal memuat summary');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [stageId, debouncedSearch]);

  useEffect(() => { loadSummary(); }, [loadSummary]);

  // Silent auto refresh every 60s
  useEffect(() => {
    const id = setInterval(() => loadSummary(true), 60000);
    return () => clearInterval(id);
  }, [loadSummary]);

  const items: any[] = useMemo(() => {
    let list = data?.items || [];
    if (needsActionOnly) list = list.filter((i: any) => i.needs_action?.length);
    if (overdueOnly) list = list.filter((i: any) => i.sla?.overdue);
    return list;
  }, [data, needsActionOnly, overdueOnly]);

  // Keep a valid selection
  useEffect(() => {
    if (!items.length) { setSelectedId(null); return; }
    if (!selectedId || !items.some((i) => i.id === selectedId)) setSelectedId(items[0].id);
  }, [items, selectedId]);

  useEffect(() => {
    if (!selectedId) { setDetail(null); return; }
    let cancelled = false;
    setDetailLoading(true);
    apiService.getSummaryTask(selectedId)
      .then((res) => { if (!cancelled) setDetail(res); })
      .catch((err) => { if (!cancelled) { console.error(err); setDetail(null); } })
      .finally(() => { if (!cancelled) setDetailLoading(false); });
    return () => { cancelled = true; };
  }, [selectedId, data?.generated_at]);

  const kpis = data?.kpis;
  const stages: any[] = (data?.stages || []).filter((s: any) => s.in_stepper);
  const cancelledStage = (data?.stages || []).find((s: any) => !s.in_stepper);

  const card = darkMode ? 'bg-gray-800/80 border-gray-700' : 'bg-white border-gray-200';
  const muted = darkMode ? 'text-gray-400' : 'text-gray-500';
  const strong = darkMode ? 'text-white' : 'text-gray-900';

  const selectRow = (id: number) => {
    setSelectedId(id);
    // On small screens the detail panel is below the table
    if (window.innerWidth < 1280) setTimeout(() => detailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };

  /* ---------------------------------------------------------------- render */
  return (
    <div className="px-6 py-6 max-w-[1600px] mx-auto">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className={`text-xs font-semibold uppercase tracking-wider mb-1 ${darkMode ? 'text-violet-400' : 'text-violet-600'}`}>
            <i className="fa-solid fa-chart-simple mr-1.5"></i>Summary
          </div>
          <h1 className={`text-2xl font-semibold ${strong}`}>Workflow Antrean</h1>
          <p className={`text-sm mt-1 ${muted}`}>
            {data?.scope === 'all' ? 'Seluruh task yang sedang berjalan' : `Task Anda & tim (user group) · ${user?.name || ''}`}
            {data?.generated_at && <span className="ml-2 opacity-70">· diperbarui {formatRelativeTime(data.generated_at)}</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <i className={`fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-sm ${muted}`}></i>
            <input
              id="summary-search"
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cari ID, PID, judul atau perusahaan"
              className={`w-80 pl-9 pr-8 py-2 text-sm rounded-lg border focus:outline-none focus:ring-2 focus:ring-violet-500 transition-colors ${darkMode ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900'}`}
            />
            {search && (
              <button onClick={() => setSearch('')} className={`absolute right-2.5 top-1/2 -translate-y-1/2 text-xs ${muted} hover:text-violet-500`} aria-label="Hapus pencarian">
                <i className="fa-solid fa-xmark"></i>
              </button>
            )}
          </div>
          <button
            id="summary-refresh"
            onClick={() => loadSummary()}
            className={`p-2.5 rounded-lg border transition-colors ${darkMode ? 'border-gray-700 text-gray-300 hover:bg-gray-800' : 'border-gray-300 text-gray-600 hover:bg-gray-100'}`}
            title="Muat ulang"
          >
            <i className={`fa-solid fa-rotate-right text-sm ${loading ? 'fa-spin' : ''}`}></i>
          </button>
        </div>
      </div>

      {error && (
        <div className={`mb-4 p-3 border-l-4 border-rose-500 rounded-lg text-sm ${darkMode ? 'bg-rose-900/20 text-rose-300' : 'bg-rose-50 text-rose-700'}`}>{error}</div>
      )}

      {/* KPI cards */}
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiCard
          id="kpi-active" darkMode={darkMode} icon="fa-layer-group" accent="violet"
          label="Antrean aktif" value={kpis ? String(kpis.active_count) : '–'}
          hint="Di luar Released & Cancelled"
          active={!needsActionOnly && !overdueOnly && stageId === null}
          onClick={() => { setStageId(null); setNeedsActionOnly(false); setOverdueOnly(false); }}
        />
        <KpiCard
          id="kpi-needs-action" darkMode={darkMode} icon="fa-hand-point-up" accent="amber"
          label="Perlu tindakan" value={kpis ? String(kpis.needs_action_count) : '–'}
          hint="Review belum resolved / Anda di-mention"
          active={needsActionOnly}
          onClick={() => { setStageId(null); setOverdueOnly(false); setNeedsActionOnly((v) => !v); }}
        />
        <KpiCard
          id="kpi-sla-overdue" darkMode={darkMode} icon="fa-stopwatch" accent={kpis?.sla_overdue_count ? 'rose' : 'emerald'}
          label="SLA terlewat" value={kpis ? String(kpis.sla_overdue_count) : '–'}
          hint={kpis?.sla_overdue_count ? 'Melewati batas waktu tahapan' : 'Semua dalam batas waktu'}
          highlight={!!kpis?.sla_overdue_count}
          active={overdueOnly}
          onClick={() => { setStageId(null); setNeedsActionOnly(false); setOverdueOnly((v) => !v); }}
        />
        <KpiCard
          id="kpi-pipeline" darkMode={darkMode} icon="fa-sack-dollar" accent="blue"
          label="Nilai pipeline" value={kpis ? formatRupiahCompact(kpis.pipeline_value) : '–'}
          hint={kpis ? (kpis.pipeline_missing_value_count ? `${kpis.pipeline_missing_value_count} task belum ada nilai` : formatRupiah(kpis.pipeline_value)) : ''}
        />
      </section>

      {/* Workflow stages */}
      <section className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <h2 className={`text-xs font-semibold uppercase tracking-wider ${muted}`}>Tahapan workflow</h2>
          {cancelledStage && (
            <button
              onClick={() => { setNeedsActionOnly(false); setOverdueOnly(false); setStageId(stageId === cancelledStage.id ? null : cancelledStage.id); }}
              className={`text-xs font-medium transition-colors ${stageId === cancelledStage.id ? 'text-rose-500' : `${muted} hover:text-rose-500`}`}
            >
              <i className="fa-solid fa-ban mr-1"></i>{cancelledStage.name} ({cancelledStage.count})
            </button>
          )}
        </div>
        <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${Math.max(stages.length, 1)}, minmax(0, 1fr))` }}>
          {stages.map((s, idx) => {
            const isActive = stageId === s.id;
            const tone = toneFor(s.name);
            return (
              <button
                key={s.id}
                id={`stage-${s.name?.toLowerCase()}`}
                onClick={() => { setNeedsActionOnly(false); setOverdueOnly(false); setStageId(isActive ? null : s.id); }}
                className={`group relative text-left rounded-xl border px-4 py-3 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md ${
                  isActive
                    ? darkMode ? 'bg-violet-900/30 border-violet-500 ring-1 ring-violet-500' : 'bg-violet-50 border-violet-500 ring-1 ring-violet-500'
                    : card
                }`}
              >
                {idx < stages.length - 1 && (
                  <i className={`hidden xl:block fa-solid fa-chevron-right absolute -right-2.5 top-1/2 -translate-y-1/2 text-[10px] z-10 ${darkMode ? 'text-gray-600' : 'text-gray-300'}`}></i>
                )}
                <div className="flex items-center justify-between">
                  <span className={`text-sm font-semibold ${isActive ? (darkMode ? 'text-violet-300' : 'text-violet-700') : strong}`}>{s.name}</span>
                  <i className={`fa-solid ${STAGE_ICONS[(s.name || '').toUpperCase()] || 'fa-circle'} text-xs ${isActive ? 'text-violet-500' : muted}`}></i>
                </div>
                <div className="flex items-end justify-between mt-2">
                  <span className={`text-2xl font-bold tabular-nums ${isActive ? (darkMode ? 'text-violet-200' : 'text-violet-700') : strong}`}>{s.count}</span>
                  <span className={`text-[11px] ${muted}`}>{s.sla_days != null ? `SLA ${s.sla_days}h` : 'item'}</span>
                </div>
                <div className={`mt-2 h-1 rounded-full overflow-hidden ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className={`h-full ${tone.dot} transition-all duration-500`} style={{ width: `${data?.kpis ? Math.min(100, (s.count / Math.max(1, ...stages.map((x) => x.count))) * 100) : 0}%` }}></div>
                </div>
              </button>
            );
          })}
        </div>
      </section>

      {/* Queue + detail */}
      <section className="grid grid-cols-1 xl:grid-cols-5 gap-6 items-start">
        {/* Queue table */}
        <div className={`xl:col-span-3 rounded-xl border overflow-hidden ${card}`}>
          <div className={`flex items-center justify-between px-5 py-3 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <h2 className={`text-sm font-semibold ${strong}`}>
              Daftar antrean
              <span className={`ml-2 px-2 py-0.5 text-xs rounded-full ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>{items.length}</span>
            </h2>
            <div className={`text-xs ${muted}`}>
              {stageId !== null && <FilterChip label={(data?.stages || []).find((s: any) => s.id === stageId)?.name} onClear={() => setStageId(null)} darkMode={darkMode} />}
              {needsActionOnly && <FilterChip label="Perlu tindakan" onClear={() => setNeedsActionOnly(false)} darkMode={darkMode} />}
              {overdueOnly && <FilterChip label="SLA terlewat" onClear={() => setOverdueOnly(false)} darkMode={darkMode} />}
              {stageId === null && !needsActionOnly && !overdueOnly && 'Semua task aktif · diurutkan dari SLA paling kritis'}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className={darkMode ? 'bg-gray-900/60' : 'bg-gray-50'}>
                <tr className={`text-left text-[11px] font-semibold uppercase tracking-wider ${muted}`}>
                  <th className="px-5 py-2.5">ID</th>
                  <th className="px-3 py-2.5">Perusahaan / Judul</th>
                  <th className="px-3 py-2.5">Status</th>
                  <th className="px-3 py-2.5 text-right">Nilai</th>
                  <th className="px-3 py-2.5">SLA</th>
                  <th className="px-3 py-2.5 w-16"></th>
                </tr>
              </thead>
              <tbody className={`divide-y ${darkMode ? 'divide-gray-700/70' : 'divide-gray-100'}`}>
                {loading && !data ? (
                  [...Array(5)].map((_, i) => (
                    <tr key={i}><td colSpan={6} className="px-5 py-3"><div className={`h-5 rounded animate-pulse ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}></div></td></tr>
                  ))
                ) : items.length === 0 ? (
                  <tr>
                    <td colSpan={6} className={`px-5 py-12 text-center text-sm ${muted}`}>
                      <i className="fa-regular fa-folder-open text-3xl mb-2 block opacity-60"></i>
                      Tidak ada task pada filter ini
                    </td>
                  </tr>
                ) : (
                  items.map((t) => {
                    const selected = t.id === selectedId;
                    return (
                      <tr
                        key={t.id}
                        id={`summary-row-${t.id}`}
                        onClick={() => selectRow(t.id)}
                        className={`cursor-pointer transition-colors ${
                          selected
                            ? darkMode ? 'bg-violet-900/25' : 'bg-violet-50/80'
                            : darkMode ? 'hover:bg-gray-700/40' : 'hover:bg-gray-50'
                        }`}
                      >
                        <td className={`px-5 py-3 text-sm font-mono whitespace-nowrap ${selected ? 'border-l-2 border-violet-500' : 'border-l-2 border-transparent'} ${muted}`}>
                          {t.pid || `#${t.id}`}
                        </td>
                        <td className="px-3 py-3 max-w-[280px]">
                          <div className={`text-sm truncate ${selected ? 'font-semibold' : 'font-medium'} ${strong}`}>{t.company || t.title}</div>
                          <div className={`text-xs truncate ${muted}`}>{t.company ? t.title : (t.agreement_type || '')}</div>
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap"><StatusBadge name={t.status?.name} darkMode={darkMode} /></td>
                        <td className={`px-3 py-3 text-sm text-right whitespace-nowrap tabular-nums ${selected ? 'font-semibold' : ''} ${strong}`}>
                          {t.agreement_amount != null ? formatRupiahCompact(t.agreement_amount) : <span className={muted}>–</span>}
                        </td>
                        <td className="px-3 py-3 text-sm whitespace-nowrap"><SlaText sla={t.sla} darkMode={darkMode} strong={selected} /></td>
                        <td className="px-3 py-3 whitespace-nowrap text-right">
                          <div className="flex items-center justify-end gap-2.5">
                            {t.needs_action?.length > 0 && (
                              <span title={t.needs_action.join('\n')} className="relative flex h-2.5 w-2.5">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-60"></span>
                                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-amber-500"></span>
                              </span>
                            )}
                            {t.comment_count > 0 && (
                              <span className={`text-xs ${muted}`} title={`${t.comment_count} komentar`}>
                                <i className="fa-regular fa-comment mr-0.5"></i>{t.comment_count}
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Detail panel */}
        <div ref={detailRef} className="xl:col-span-2 xl:sticky xl:top-20">
          <h2 className={`text-xs font-semibold uppercase tracking-wider mb-2 xl:hidden ${muted}`}>Detail item terpilih</h2>
          <div className={`rounded-xl border ${card} ${detailLoading ? 'opacity-70' : ''} transition-opacity`}>
            {!detail ? (
              <div className={`p-10 text-center text-sm ${muted}`}>
                {detailLoading ? <i className="fa-solid fa-spinner fa-spin text-xl"></i> : (<><i className="fa-regular fa-hand-pointer text-2xl mb-2 block opacity-60"></i>Pilih task untuk melihat detail</>)}
              </div>
            ) : (
              <TaskDetailPanel detail={detail} darkMode={darkMode} onOpen={() => navigate(`/agreement/${detail.id}`)} />
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

/* ------------------------------------------------------------------ sub components */

const ACCENTS: Record<string, { iconLight: string; iconDark: string }> = {
  violet: { iconLight: 'bg-violet-100 text-violet-600', iconDark: 'bg-violet-900/50 text-violet-300' },
  amber: { iconLight: 'bg-amber-100 text-amber-600', iconDark: 'bg-amber-900/50 text-amber-300' },
  rose: { iconLight: 'bg-rose-100 text-rose-600', iconDark: 'bg-rose-900/50 text-rose-300' },
  emerald: { iconLight: 'bg-emerald-100 text-emerald-600', iconDark: 'bg-emerald-900/50 text-emerald-300' },
  blue: { iconLight: 'bg-blue-100 text-blue-600', iconDark: 'bg-blue-900/50 text-blue-300' },
};

const KpiCard: React.FC<{
  id: string; darkMode: boolean; icon: string; accent: string; label: string; value: string; hint?: string;
  highlight?: boolean; active?: boolean; onClick?: () => void;
}> = ({ id, darkMode, icon, accent, label, value, hint, highlight, active, onClick }) => {
  const a = ACCENTS[accent] || ACCENTS.violet;
  const base = highlight
    ? darkMode ? 'bg-gradient-to-br from-rose-900/40 to-rose-800/20 border-rose-700' : 'bg-gradient-to-br from-rose-50 to-white border-rose-200'
    : darkMode ? 'bg-gray-800/80 border-gray-700' : 'bg-white border-gray-200';
  const Comp: any = onClick ? 'button' : 'div';
  return (
    <Comp
      id={id}
      onClick={onClick}
      className={`text-left rounded-xl border p-4 transition-all duration-200 ${base} ${onClick ? 'hover:-translate-y-0.5 hover:shadow-md cursor-pointer' : ''} ${active ? 'ring-2 ring-violet-500' : ''}`}
    >
      <div className="flex items-start justify-between">
        <span className={`text-xs font-medium ${highlight ? (darkMode ? 'text-rose-300' : 'text-rose-600') : darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{label}</span>
        <span className={`w-8 h-8 rounded-lg flex items-center justify-center ${darkMode ? a.iconDark : a.iconLight}`}>
          <i className={`fa-solid ${icon} text-sm`}></i>
        </span>
      </div>
      <div className={`text-2xl font-bold mt-1 tabular-nums ${highlight ? (darkMode ? 'text-rose-200' : 'text-rose-700') : darkMode ? 'text-white' : 'text-gray-900'}`}>{value}</div>
      {hint && <div className={`text-[11px] mt-1 truncate ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>{hint}</div>}
    </Comp>
  );
};

const FilterChip: React.FC<{ label?: string; onClear: () => void; darkMode: boolean }> = ({ label, onClear, darkMode }) => (
  <span className={`inline-flex items-center gap-1 ml-1 px-2 py-0.5 rounded-full text-xs font-medium ${darkMode ? 'bg-violet-900/40 text-violet-300' : 'bg-violet-100 text-violet-700'}`}>
    {label}
    <button onClick={onClear} className="hover:opacity-70" aria-label={`Hapus filter ${label}`}><i className="fa-solid fa-xmark text-[10px]"></i></button>
  </span>
);

const TaskDetailPanel: React.FC<{ detail: any; darkMode: boolean; onOpen: () => void }> = ({ detail, darkMode, onOpen }) => {
  const [commentTab, setCommentTab] = useState<'all' | 'task' | 'review'>('all');
  const muted = darkMode ? 'text-gray-400' : 'text-gray-500';
  const strong = darkMode ? 'text-white' : 'text-gray-900';
  const divider = darkMode ? 'border-gray-700' : 'border-gray-200';
  const steps: any[] = detail.steps || [];
  const isCancelled = (detail.status?.name || '').toUpperCase() === 'CANCELLED';
  const comments: any[] = (detail.comments || []).filter((c: any) => commentTab === 'all' || c.type === commentTab);
  const commercial = detail.commercial || {};

  return (
    <div>
      {/* Header */}
      <div className={`px-5 py-4 border-b ${divider}`}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className={`text-xs font-mono ${muted}`}>{detail.pid || `#${detail.id}`}{detail.agreement_type ? ` · ${detail.agreement_type}` : ''}</div>
            <h3 className={`text-base font-semibold truncate ${strong}`}>{detail.company || detail.title}</h3>
            {detail.company && <p className={`text-sm truncate ${muted}`}>{detail.title}</p>}
          </div>
          <StatusBadge name={detail.status?.name} darkMode={darkMode} />
        </div>
        <div className={`flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs ${muted}`}>
          <span><i className="fa-regular fa-user mr-1"></i>{detail.creator || '-'}</span>
          <span><i className="fa-regular fa-calendar mr-1"></i>{formatDate(detail.created_at, { hour: undefined, minute: undefined })}</span>
          <span className="inline-flex items-center gap-1">SLA: <SlaText sla={detail.sla} darkMode={darkMode} /></span>
        </div>
      </div>

      {/* Stepper */}
      <div className={`px-5 py-5 border-b ${divider}`}>
        {isCancelled && (
          <div className={`mb-3 text-xs font-medium px-3 py-2 rounded-lg ${darkMode ? 'bg-rose-900/30 text-rose-300' : 'bg-rose-50 text-rose-700'}`}>
            <i className="fa-solid fa-ban mr-1.5"></i>Task ini dibatalkan
          </div>
        )}
        <div className="flex items-start">
          {steps.map((s, i) => {
            const done = s.state === 'done';
            const current = s.state === 'current';
            const overdue = current && detail.sla?.overdue;
            const nextDone = i < steps.length - 1 && (steps[i + 1].state === 'done' || steps[i + 1].state === 'current');
            return (
              <div key={s.id} className="flex-1 flex flex-col items-center relative min-w-0" title={s.entered_at ? `Masuk ${formatDate(s.entered_at)}${s.changed_by ? ` oleh ${s.changed_by}` : ''}` : ''}>
                {i < steps.length - 1 && (
                  <div className={`absolute top-3.5 left-1/2 w-full h-0.5 ${nextDone ? 'bg-violet-500' : darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}></div>
                )}
                <div className={`relative z-10 w-7 h-7 rounded-full flex items-center justify-center border-2 transition-all ${
                  done ? 'bg-violet-500 border-violet-500 text-white'
                    : current ? overdue
                      ? darkMode ? 'bg-rose-900/50 border-rose-500 text-rose-300 ring-4 ring-rose-500/20' : 'bg-rose-50 border-rose-500 text-rose-600 ring-4 ring-rose-100'
                      : darkMode ? 'bg-violet-900/50 border-violet-400 text-violet-300 ring-4 ring-violet-500/20' : 'bg-white border-violet-500 text-violet-600 ring-4 ring-violet-100'
                    : darkMode ? 'bg-gray-800 border-gray-600 text-gray-500' : 'bg-white border-gray-300 text-gray-400'
                }`}>
                  {done ? <i className="fa-solid fa-check text-[11px]"></i> : current ? <span className={`w-2 h-2 rounded-full ${overdue ? 'bg-rose-500' : 'bg-violet-500'}`}></span> : <span className="text-[10px] font-semibold">{i + 1}</span>}
                </div>
                <div className={`mt-2 text-[11px] text-center truncate w-full px-0.5 ${current ? `font-semibold ${strong}` : done ? (darkMode ? 'text-gray-300' : 'text-gray-600') : muted}`}>{s.name}</div>
                {s.entered_at && (done || current) && <div className={`text-[10px] ${muted}`}>{formatRelativeTime(s.entered_at)}</div>}
              </div>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 2xl:grid-cols-2">
        {/* Comments */}
        <div className={`px-5 py-4 border-b 2xl:border-b-0 2xl:border-r ${divider}`}>
          <div className="flex items-center justify-between mb-3">
            <h4 className={`text-sm font-semibold ${strong}`}>
              Komentar ({detail.comments?.length || 0})
              {detail.unresolved_review_count > 0 && (
                <span className={`ml-2 px-1.5 py-0.5 text-[10px] font-semibold rounded ${darkMode ? 'bg-amber-900/40 text-amber-300' : 'bg-amber-100 text-amber-700'}`}>
                  {detail.unresolved_review_count} belum resolved
                </span>
              )}
            </h4>
            <div className={`flex text-[11px] rounded-md p-0.5 ${darkMode ? 'bg-gray-900' : 'bg-gray-100'}`}>
              {(['all', 'task', 'review'] as const).map((k) => (
                <button key={k} onClick={() => setCommentTab(k)}
                  className={`px-2 py-0.5 rounded ${commentTab === k ? (darkMode ? 'bg-gray-700 text-white' : 'bg-white text-gray-900 shadow-sm') : muted}`}>
                  {k === 'all' ? 'Semua' : k === 'task' ? 'Diskusi' : 'Review'}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
            {comments.length === 0 ? (
              <p className={`text-sm py-4 text-center ${muted}`}>Belum ada komentar</p>
            ) : comments.map((c) => (
              <div key={c.id} className="flex gap-2.5">
                <Avatar src={c.avatar_url} name={c.user} id={c.user_id} className="w-7 h-7 rounded-full flex-shrink-0" alt={c.user || 'User'} />
                <div className="min-w-0 flex-1">
                  <div className={`text-[11px] ${muted}`}>
                    <span className={`font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{c.user || 'Unknown'}</span>
                    {c.role && <> · {c.role}</>} · {formatRelativeTime(c.created_at)}
                    {c.type === 'review' && (
                      <span className={`ml-1.5 px-1.5 py-px rounded text-[10px] font-semibold ${
                        c.is_resolved
                          ? darkMode ? 'bg-emerald-900/40 text-emerald-300' : 'bg-emerald-50 text-emerald-700'
                          : darkMode ? 'bg-amber-900/40 text-amber-300' : 'bg-amber-50 text-amber-700'
                      }`}>
                        {c.is_reply ? 'Balasan review' : c.is_resolved ? 'Resolved' : 'Review'}
                      </span>
                    )}
                  </div>
                  {c.selected_text && (
                    <div className={`mt-1 text-[11px] italic border-l-2 pl-2 truncate ${darkMode ? 'border-gray-600 text-gray-500' : 'border-gray-300 text-gray-400'}`}>“{c.selected_text}”</div>
                  )}
                  <p className={`text-sm mt-0.5 break-words whitespace-pre-wrap ${strong}`}>{c.content}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Commercial */}
        <div className="px-5 py-4">
          <h4 className={`text-sm font-semibold mb-3 ${strong}`}>Komersial{detail.template_type ? ` · ${detail.template_type}` : ''}</h4>
          <dl className="space-y-2.5 text-sm">
            {[
              ['Metode Pembayaran', commercial.payment_method],
              ['Jangka Waktu', commercial.period],
              ['Jaminan', commercial.guarantee],
              ['Penalti / Kompensasi', commercial.penalty_compensation],
            ].map(([label, value]) => (
              <div key={label as string} className="flex justify-between gap-4">
                <dt className={`flex-shrink-0 ${muted}`}>{label}</dt>
                <dd className={`text-right break-words min-w-0 ${value ? strong : muted}`}>{value || '-'}</dd>
              </div>
            ))}
          </dl>
          <div className={`flex justify-between items-baseline mt-4 pt-3 border-t ${divider}`}>
            <span className={`text-sm font-semibold ${strong}`}>Nilai Perjanjian</span>
            <span className={`text-lg font-bold tabular-nums ${strong}`}>
              {commercial.agreement_amount != null ? formatRupiah(commercial.agreement_amount) : (commercial.agreement_value || '-')}
            </span>
          </div>
          <button
            id="summary-open-task"
            onClick={onOpen}
            className="mt-4 w-full px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white text-sm font-medium rounded-lg transition-colors"
          >
            Buka Task <i className="fa-solid fa-arrow-right ml-1.5"></i>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Summary;
